import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import HistoryIcon from '@mui/icons-material/History';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { organizationService } from '../services/organizationService';
import { billingService, BillingPlanTier, BillingPricePlan } from '../services/billingService';
import { AuthStore, useAuthStore } from '../store/auth.store';

const PLAN_ORDER: BillingPlanTier[] = ['STARTER', 'BASIC', 'PRO'];

const PLAN_COPY: Record<BillingPlanTier, { title: string; description: string; features: string[] }> = {
  STARTER: {
    title: 'Starter',
    description: 'Baseline technical visibility for one growing site.',
    features: ['Core readiness tracking', 'Site diagnostics', 'Action workspace'],
  },
  BASIC: {
    title: 'Basic',
    description: 'A practical upgrade for active optimization work.',
    features: ['Expanded reporting', 'Notification schedules', 'Priority checks'],
  },
  PRO: {
    title: 'Pro',
    description: 'Full workflow coverage for teams managing momentum.',
    features: ['Advanced report coverage', 'Higher plan limits', 'Workflow acceleration'],
  },
};

const normalizePlanTier = (planTier?: string): BillingPlanTier => {
  const normalized = (planTier || '').trim().toUpperCase();
  if (normalized === 'FREE') return 'STARTER';
  if (PLAN_ORDER.includes(normalized as BillingPlanTier)) return normalized as BillingPlanTier;
  return 'STARTER';
};

const planRank = (plan: BillingPlanTier) => PLAN_ORDER.indexOf(plan);

const isBillingPlanTier = (value: string): value is BillingPlanTier => PLAN_ORDER.includes(value as BillingPlanTier);

const formatMoney = (amountMinor: number, currency: string) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amountMinor / 100);

const formatDateTime = (value?: string) => (value ? new Date(value).toLocaleString() : '-');

const planTitle = (plan?: string) => {
  const normalized = (plan || '').trim().toUpperCase();
  if (normalized === 'BASIC') return 'BASIC Plan';
  if (normalized === 'PRO') return 'PRO Plan';
  if (normalized === 'STARTER' || normalized === 'FREE') return 'STARTER Plan';
  return normalized ? `${normalized} Plan` : '-';
};

const formatPlanPrice = (amountMinor: number, currency: string) => `${currency} ${amountMinor / 100}`;

const isPaidRequiredNow = (flowType?: string, paidAmountMinor?: number) => {
  const normalized = (flowType || '').trim().toUpperCase();
  if (normalized === 'PAID_REQUIRED_NOW') return true;
  if (normalized === 'PROMO_ZERO' || normalized === 'TRIAL_ACTIVE') return false;
  return (paidAmountMinor ?? 0) > 0;
};

const BillingPage: React.FC = () => {
  const user = useAuthStore((state: AuthStore) => state.user);
  const getEffectiveTenantId = useAuthStore((state: AuthStore) => state.getEffectiveTenantId);
  const tenantId = getEffectiveTenantId();
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [planChangeMessage, setPlanChangeMessage] = useState<string | null>(null);
  const [paymentReturnStatus, setPaymentReturnStatus] = useState<'success' | 'cancelled' | null>(null);

  const organizationQuery = useQuery({
    queryKey: ['billing-organization', tenantId],
    queryFn: () => organizationService.getOrganization(String(tenantId)),
    enabled: Boolean(tenantId),
    staleTime: 60_000,
  });

  const tenantReference = organizationQuery.data?.id || tenantId;

  const subscriptionQuery = useQuery({
    queryKey: ['billing-subscription', tenantReference],
    queryFn: () => billingService.getSubscription(),
    enabled: Boolean(tenantReference),
    staleTime: 30_000,
    retry: false,
  });

  const currentPlan = normalizePlanTier(subscriptionQuery.data?.currentPlan || organizationQuery.data?.planTier);
  const pendingPlan = subscriptionQuery.data?.pendingPlan ? normalizePlanTier(subscriptionQuery.data.pendingPlan) : null;
  const subscriptionStatus = subscriptionQuery.data?.status || 'ACTIVE';
  const billingPeriodEnd = subscriptionQuery.data?.billingPeriodEnd;
  const freePeriodEnd = subscriptionQuery.data?.freePeriodEnd;
  const freePeriodExtensionCount = subscriptionQuery.data?.freePeriodExtensionCount || 0;
  const pendingChangeEffectiveAt = subscriptionQuery.data?.pendingChangeEffectiveAt;

  const historyQuery = useQuery({
    queryKey: ['billing-history', tenantReference],
    queryFn: () => billingService.getBillingHistory(),
    enabled: Boolean(tenantReference),
    staleTime: 30_000,
    retry: false,
  });

  const refetchOrganization = organizationQuery.refetch;
  const refetchSubscription = subscriptionQuery.refetch;
  const refetchHistory = historyQuery.refetch;

  const pricePlansQuery = useQuery({
    queryKey: ['billing-price-plans', tenantReference, 'customer'],
    queryFn: () => billingService.getPricePlans('customer'),
    enabled: Boolean(tenantReference),
    staleTime: 60_000,
    retry: false,
  });

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const paymentStatus = searchParams.get('payment');
    if (paymentStatus !== 'success' && paymentStatus !== 'cancelled') {
      return;
    }

    setPaymentReturnStatus(paymentStatus);
    setActiveTab(paymentStatus === 'success' ? 2 : 0);
    const providerCheckoutReference = searchParams.get('session') || searchParams.get('subscription_id') || searchParams.get('token');
    const isFreeCheckout = searchParams.get('freeCheckout') === 'true';
    if (paymentStatus === 'success' && providerCheckoutReference && !isFreeCheckout) {
      billingService.completeCheckoutSession(providerCheckoutReference)
        .catch((error) => {
          setPaymentError(error instanceof Error ? error.message : 'Payment was approved, but completion failed.');
        })
        .finally(() => {
          void refetchOrganization();
          void refetchSubscription();
          void refetchHistory();
        });
    } else if (paymentStatus === 'cancelled' && providerCheckoutReference) {
      billingService.cancelCheckoutSession(providerCheckoutReference)
        .catch((error) => {
          setPaymentError(error instanceof Error ? error.message : 'Checkout was cancelled, but status update failed.');
        })
        .finally(() => {
          void refetchOrganization();
          void refetchSubscription();
          void refetchHistory();
        });
    } else {
      void refetchOrganization();
      void refetchSubscription();
      void refetchHistory();
    }

    const refreshTimers = paymentStatus === 'success'
      ? [window.setTimeout(() => {
          void refetchOrganization();
          void refetchSubscription();
          void refetchHistory();
        }, 2500), window.setTimeout(() => {
          void refetchOrganization();
          void refetchSubscription();
          void refetchHistory();
        }, 7000)]
      : [];

    navigate('/billing', { replace: true });

    return () => {
      refreshTimers.forEach(window.clearTimeout);
    };
  }, [location.search, navigate, refetchOrganization, refetchSubscription, refetchHistory]);

  const upgradeMutation = useMutation({
    mutationFn: async ({ targetPlan, promoCode }: { targetPlan: BillingPlanTier; promoCode?: string }) => {
      return billingService.createCheckoutSession({
        targetPlan,
        promoCode,
      });
    },
    onSuccess: (session) => {
      setPaymentError(null);
      const paidRequiredNow = isPaidRequiredNow(session.flowType, session.paidAmountMinor);
      const nextUrl = paidRequiredNow
        ? (session.providerRedirectUrl || session.paymentOnlineUrl)
        : (session.paymentOnlineUrl || session.providerRedirectUrl);

      if (!nextUrl) {
        setPaymentError('Checkout session created, but no redirect URL was returned. Please refresh billing status.');
        void refetchOrganization();
        void refetchSubscription();
        void refetchHistory();
        return;
      }

      window.location.assign(nextUrl);
    },
    onError: (error) => {
      setPaymentError(error instanceof Error ? error.message : 'Failed to start payment.');
    },
  });

  const schedulePlanChangeMutation = useMutation({
    mutationFn: (targetPlan: BillingPlanTier) => billingService.schedulePlanChange(targetPlan),
    onSuccess: (subscription) => {
      setPaymentError(null);
      setPlanChangeMessage(`${subscription.pendingPlan || 'Plan change'} scheduled for ${formatDateTime(subscription.pendingChangeEffectiveAt)}.`);
      void refetchOrganization();
      void refetchSubscription();
      void refetchHistory();
    },
    onError: (error) => {
      setPlanChangeMessage(null);
      setPaymentError(error instanceof Error ? error.message : 'Failed to schedule plan change.');
    },
  });

  const extendFreeSubscriptionMutation = useMutation({
    mutationFn: () => billingService.extendFreeSubscription({
      extensionDays: 30,
      reason: 'Customer requested free subscription extension from billing portal.',
    }),
    onSuccess: (subscription) => {
      setPaymentError(null);
      setPlanChangeMessage(`Free subscription extended until ${formatDateTime(subscription.freePeriodEnd)}.`);
      void organizationQuery.refetch();
      void subscriptionQuery.refetch();
    },
    onError: (error) => {
      setPlanChangeMessage(null);
      setPaymentError(error instanceof Error ? error.message : 'Failed to extend free subscription.');
    },
  });

  const plans = useMemo(() => {
    const visiblePlans = (pricePlansQuery.data ?? [])
      .filter((plan): plan is BillingPricePlan & { productCode: BillingPlanTier } => isBillingPlanTier(plan.productCode))
      .sort((left, right) => left.displayOrder - right.displayOrder);

    return visiblePlans.map((plan) => {
      const defaultPromo = plan.availablePromoCodes?.find((promo) => promo.defaultPromo)
        ?? plan.availablePromoCodes?.find((promo) => promo.promoCode === plan.defaultPromoCode);

      return {
        tier: plan.productCode,
        title: PLAN_COPY[plan.productCode]?.title ?? plan.displayName,
        description: PLAN_COPY[plan.productCode]?.description ?? '',
        features: PLAN_COPY[plan.productCode]?.features ?? [],
        amountMinor: plan.effectivePriceMinor,
        normalAmountMinor: plan.normalPriceMinor,
        currency: plan.currency,
        billingPeriod: plan.billingPeriod,
        customerSelectable: plan.customerSelectable,
        defaultPromoCode: defaultPromo?.promoCode ?? plan.defaultPromoCode,
        defaultPromoName: defaultPromo?.promoName || defaultPromo?.promoCode || plan.defaultPromoCode,
      };
    });
  }, [pricePlansQuery.data]);

  if (!tenantId) {
    return <Alert severity="warning">Tenant context is required to load billing.</Alert>;
  }

  return (
    <Box sx={{ maxWidth: 1180, mx: 'auto' }}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={2} sx={{ mb: 2.5 }}>
        <Box>
          <Typography variant="h4" fontWeight={800}>Billing</Typography>
          <Typography variant="body2" color="text.secondary">
            Manage plan billing, PayPal payment, and payment history for {organizationQuery.data?.name || user?.tenantName || 'your organization'}.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Chip icon={<WorkspacePremiumIcon />} label={`Current plan: ${currentPlan}`} color="primary" variant="outlined" />
          {pendingPlan && <Chip label={`Pending: ${pendingPlan}`} color="warning" variant="outlined" />}
        </Stack>
      </Stack>

      {organizationQuery.isLoading && (
        <Box display="flex" alignItems="center" gap={1} py={2}>
          <CircularProgress size={20} />
          <Typography variant="body2">Loading billing context...</Typography>
        </Box>
      )}

      {organizationQuery.error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load organization plan: {organizationQuery.error instanceof Error ? organizationQuery.error.message : 'Unknown error'}
        </Alert>
      )}

      {subscriptionQuery.error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Billing subscription state is not available: {subscriptionQuery.error instanceof Error ? subscriptionQuery.error.message : 'Unknown error'}
        </Alert>
      )}

      {paymentError && <Alert severity="error" sx={{ mb: 2 }}>{paymentError}</Alert>}

      {pricePlansQuery.error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Price plans are not available: {pricePlansQuery.error instanceof Error ? pricePlansQuery.error.message : 'Unknown error'}
        </Alert>
      )}

      {planChangeMessage && <Alert severity="success" sx={{ mb: 2 }}>{planChangeMessage}</Alert>}

      {paymentReturnStatus === 'success' && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Payment returned from PayPal. Billing history and plan status are refreshing.
        </Alert>
      )}

      {paymentReturnStatus === 'cancelled' && (
        <Alert severity="info" sx={{ mb: 2 }}>
          PayPal checkout was cancelled. Your plan has not changed.
        </Alert>
      )}

      <Paper variant="outlined" sx={{ mb: 2 }}>
        <Tabs value={activeTab} onChange={(_, value) => setActiveTab(value)} variant="scrollable" scrollButtons="auto">
          <Tab icon={<ReceiptLongIcon />} iconPosition="start" label="Bill" />
          <Tab icon={<CreditCardIcon />} iconPosition="start" label="Payment" />
          <Tab icon={<HistoryIcon />} iconPosition="start" label="History" />
        </Tabs>
      </Paper>

      {activeTab === 0 && subscriptionQuery.data && (
        <Paper variant="outlined" sx={{ mb: 2, p: 2 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between">
            <Box>
              <Typography variant="caption" color="text.secondary">Current subscription</Typography>
              <Typography variant="subtitle2" fontWeight={800}>{currentPlan} · {subscriptionStatus}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Subscription end date</Typography>
              <Typography variant="subtitle2" fontWeight={800}>{formatDateTime(billingPeriodEnd)}</Typography>
            </Box>
            {subscriptionStatus === 'TRIALING' && (
              <Box>
                <Typography variant="caption" color="text.secondary">Free until</Typography>
                <Typography variant="subtitle2" fontWeight={800}>{formatDateTime(freePeriodEnd)}</Typography>
                <Typography variant="caption" color="text.secondary">Extensions: {freePeriodExtensionCount}</Typography>
              </Box>
            )}
            {pendingPlan && (
              <Box>
                <Typography variant="caption" color="text.secondary">Scheduled change</Typography>
                <Typography variant="subtitle2" fontWeight={800}>{pendingPlan} on {formatDateTime(pendingChangeEffectiveAt)}</Typography>
              </Box>
            )}
          </Stack>
          {subscriptionStatus === 'TRIALING' && currentPlan !== 'STARTER' && (
            <Box sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                disabled={extendFreeSubscriptionMutation.isPending || subscriptionQuery.isLoading}
                onClick={() => extendFreeSubscriptionMutation.mutate()}
              >
                {extendFreeSubscriptionMutation.isPending ? 'Extending...' : 'Extend free period by 30 days'}
              </Button>
            </Box>
          )}
        </Paper>
      )}

      {activeTab === 0 && (
        pricePlansQuery.isLoading ? (
          <Box display="flex" alignItems="center" gap={1} py={3}>
            <CircularProgress size={22} />
            <Typography variant="body2">Loading price plans...</Typography>
          </Box>
        ) : plans.length === 0 ? (
          <Alert severity="info">No customer price plans are currently available.</Alert>
        ) : (
        <Grid container spacing={2}>
          {plans.map((plan) => {
            const isCurrent = plan.tier === currentPlan;
            const isUpgrade = planRank(plan.tier) > planRank(currentPlan);
            const isDowngrade = planRank(plan.tier) < planRank(currentPlan);
            const isPending = pendingPlan === plan.tier;
            const canCheckout = plan.customerSelectable && isUpgrade;

            return (
              <Grid item xs={12} md={4} key={plan.tier}>
                <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column', borderColor: isCurrent ? 'primary.main' : 'divider' }}>
                  <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                    {/* Row 1: title + badge — fixed height */}
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1, minHeight: 32 }}>
                      <Typography variant="h6" fontWeight={800}>{plan.title}</Typography>
                      {isCurrent && <Chip size="small" label="Current Plan" color="primary" />}
                      {isPending && <Chip size="small" label="Scheduled" color="warning" variant="outlined" />}
                      {isDowngrade && !isPending && <Chip size="small" label="After cycle" variant="outlined" />}
                    </Stack>

                    {/* Row 2: price — fixed height */}
                    <Typography variant="h4" fontWeight={800} sx={{ mb: 0.5 }}>
                      {plan.amountMinor === 0 ? 'Free' : formatMoney(plan.amountMinor, plan.currency)}
                    </Typography>

                    {/* Row 3: strikethrough original price — always occupies space */}
                    <Typography variant="caption" color="text.secondary" sx={{ textDecoration: 'line-through', display: 'block', minHeight: 18 }}>
                      {plan.normalAmountMinor > plan.amountMinor ? formatMoney(plan.normalAmountMinor, plan.currency) : '\u00A0'}
                    </Typography>

                    {/* Row 4: per month / free label — fixed height */}
                    <Typography variant="caption" color="text.secondary" sx={{ minHeight: 18, display: 'block' }}>
                      {plan.amountMinor > 0 ? 'per month' : '\u00A0'}
                    </Typography>

                    {/* Row 5: promo name — always occupies space */}
                    <Typography variant="caption" color="success.main" display="block" sx={{ minHeight: 18, mb: 1.5 }}>
                      {plan.defaultPromoName ? `Promo: ${plan.defaultPromoName}` : '\u00A0'}
                    </Typography>

                    {/* Row 6: description — fixed height */}
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, minHeight: 40 }}>{plan.description}</Typography>

                    {/* Row 7: contextual info box — always occupies fixed height */}
                    <Box sx={{ minHeight: 64, mb: 2 }}>
                      {isCurrent && (
                        <Box sx={{ bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.light', borderRadius: 1.5, p: 1.25 }}>
                          <Typography variant="caption" color="text.secondary">Subscription end date</Typography>
                          <Typography variant="body2" fontWeight={800}>{formatDateTime(billingPeriodEnd)}</Typography>
                        </Box>
                      )}
                      {isUpgrade && currentPlan !== 'STARTER' && (
                        <Box sx={{ bgcolor: 'info.50', border: '1px solid', borderColor: 'info.light', borderRadius: 1.5, p: 1.25 }}>
                          <Typography variant="body2" color="text.secondary">
                            Upgrade takes effect immediately. Any unused amount from your current subscription will be processed as a pro-rated refund by {formatDateTime(billingPeriodEnd)}.
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    <Divider sx={{ mb: 1.5 }} />

                    {/* Row 8: features list — grows to fill remaining space */}
                    <Stack spacing={0.9} sx={{ flex: 1 }}>
                      {plan.features.map((feature) => (
                        <Typography key={feature} variant="body2">{feature}</Typography>
                      ))}
                    </Stack>
                  </CardContent>
                  <CardActions sx={{ px: 2, pb: 2 }}>
                    {isCurrent ? (
                      <Button fullWidth variant="outlined" disabled>Current Plan</Button>
                    ) : isUpgrade ? (
                      <Button
                        fullWidth
                        variant="contained"
                        disabled={!canCheckout || upgradeMutation.isPending || organizationQuery.isLoading || subscriptionQuery.isLoading}
                        onClick={() => upgradeMutation.mutate({ targetPlan: plan.tier, promoCode: plan.defaultPromoCode ?? undefined })}
                      >
                        {!plan.customerSelectable ? 'Contact Support' : upgradeMutation.isPending ? 'Opening PayPal...' : `Upgrade to ${plan.title}`}
                      </Button>
                    ) : (
                      <Button
                        fullWidth
                        variant="outlined"
                        disabled={schedulePlanChangeMutation.isPending || isPending || subscriptionQuery.isLoading}
                        onClick={() => schedulePlanChangeMutation.mutate(plan.tier)}
                      >
                        {isPending ? 'Downgrade Scheduled' : schedulePlanChangeMutation.isPending ? 'Scheduling...' : `Schedule ${plan.title}`}
                      </Button>
                    )}
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
        )
      )}

      {activeTab === 1 && (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" fontWeight={800} gutterBottom>Payment</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              PayPal checkout opens when a higher plan is selected from the Bill tab. Successful payments are stored for billing history.
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Chip label="Provider: PayPal" color="primary" variant="outlined" />
              <Chip label={`Tenant: ${tenantReference}`} variant="outlined" />
              <Chip label={`Customer: ${user?.email || user?.id || '-'}`} variant="outlined" />
            </Stack>
          </CardContent>
        </Card>
      )}

      {activeTab === 2 && (
        <Card variant="outlined">
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
              <Typography variant="h6" fontWeight={800}>Payment History</Typography>
              <Button size="small" onClick={() => historyQuery.refetch()} disabled={historyQuery.isFetching}>Refresh</Button>
            </Stack>
            {historyQuery.isLoading ? (
              <Box display="flex" alignItems="center" gap={1} py={3}>
                <CircularProgress size={22} />
                <Typography variant="body2">Loading payment history...</Typography>
              </Box>
            ) : historyQuery.error ? (
              <Alert severity="warning">
                Payment history is not available: {historyQuery.error instanceof Error ? historyQuery.error.message : 'Unknown error'}
              </Alert>
            ) : !historyQuery.data || historyQuery.data.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No payment history found.</Typography>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Plan / Price</TableCell>
                      <TableCell>Amount</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Provider Ref</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {historyQuery.data.map((item) => {
                      const originalAmountMinor = item.originalAmountMinor ?? item.amountMinor;
                      const promotionAmountMinor = item.promotionAmountMinor ?? 0;
                      const paidAmountMinor = item.paidAmountMinor ?? item.amountMinor;

                      return (
                        <TableRow key={item.checkoutSessionToken} hover>
                          <TableCell>{formatDateTime(item.paidAt || item.createdAt)}</TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>{planTitle(item.targetPlan || item.productCode)}</Typography>
                            <Typography variant="caption" color="text.secondary">{formatPlanPrice(originalAmountMinor, item.currency)}</Typography>
                            {promotionAmountMinor > 0 && (
                              <Typography variant="caption" color="success.main" display="block">
                                Promotion: {item.promotionName || item.promoCode || 'Applied'} (-{formatMoney(promotionAmountMinor, item.currency)})
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>{formatMoney(paidAmountMinor, item.currency)}</TableCell>
                          <TableCell><Chip size="small" label={item.status} variant="outlined" /></TableCell>
                          <TableCell>
                            <Typography variant="caption" sx={{ wordBreak: 'break-all' }}>{item.providerPaymentReference || '-'}</Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default BillingPage;